/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import React from 'react';
import styled from 'styled-components';
import { BladeHeader } from './BladeHeader';
import { BladeProps } from './types';

type BladeBodyProps = {
    collapsed: boolean;
};
const BladeBody = styled.section`
    display: block;
    position: absolute;
    top: calc(3rem + 2px);
    left: 0;
    width: 100%;
    height: calc(100% - (3rem + 2px));
    overflow: hidden auto;
    padding: 1rem;
    margin: 0;
    box-sizing: border-box;
    ${(props: BladeBodyProps) => (props.collapsed ? 'display: none;' : '')}
`;
BladeBody.displayName = 'BladeBody';

type BladeContainerProps = {
    collapsed: boolean;
    expanded: boolean;
    fluid: boolean;
};
const BladeContainer = styled.article`
    display: block;
    position: relative;
    height: 100%;
    box-sizing: border-box;
    border-right: 1px solid var(--plain__disabled__front__default);
    overflow: hidden;
    flex: none;
    ${(props: BladeContainerProps) =>
        props.collapsed
            ? `
    flex: none;
    min-width: 3rem;
    max-width: 3rem;
    `
            : ''}
    ${(props: BladeContainerProps) =>
        props.expanded || props.fluid
            ? `
    flex: auto;
    width: 100%;
    `
            : ''}
`;
BladeContainer.displayName = 'BladeContainer';

export const Blade = React.forwardRef<HTMLDivElement, BladeProps>(
    (props, ref) => {
        const {
            children,
            collapsed = false,
            expanded = false,
            fluid = false,
            footer,
            onActivate,
            onExpand,
            onCollapse,
            onRestore,
            initialWidth,
            ...rest
        } = props;

        const styleProp = collapsed ? null : { minWidth: initialWidth };

        return (
            <BladeContainer
                ref={ref}
                {...rest}
                onMouseDown={collapsed ? onActivate : undefined}
                style={styleProp}
                expanded={expanded}
                collapsed={collapsed}
                fluid={fluid}
            >
                <BladeHeader
                    {...rest}
                    collapsed={collapsed}
                    expanded={expanded}
                    onExpand={onExpand}
                    onCollapse={onCollapse}
                    onRestore={onRestore}
                    onActivate={onActivate}
                />
                <BladeBody collapsed={collapsed} onMouseDown={onActivate}>
                    {children}
                </BladeBody>
                {footer}
            </BladeContainer>
        );
    },
);

Blade.displayName = 'Blade';
